import type * as React from 'react';
import styled from 'styled-components';

import * as Cell from '@/components/code-illustrations/cells';
import { toPath, type ViewBox } from '@/utils/svg';

import { RETENTION_CSV } from './data';

const [, ...body] = RETENTION_CSV.split('\n');
const rows = body.map(row => row.split(',').map(v => parseFloat(v)));

const movingAvgRetentionOverChurnTime = rows.map(
  ([churnTime, , movingAvgRetention]) => [churnTime, movingAvgRetention]
);
const retentionAggOverChurnTime = rows.map(([churnTime, retentionAgg]) => [
  churnTime,
  retentionAgg
]);

const movingAvgRetentionViewbox: ViewBox = [16, 0, 42 - 16, 13];
const movingAvgRetentionPath = toPath(movingAvgRetentionOverChurnTime, {
  rangeY: [0, 13]
});

const retentionAggPathViewBox: ViewBox = [16, 0, 42 - 16, 14];
const retentionAggPath = toPath(retentionAggOverChurnTime, {
  rangeY: [0, 14]
});

const GraphArea = styled.div`
  grid-area: graph;
  display: grid;

  & > * {
    grid-area: 1 / 1;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 2em;
`;

const GraphOuter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  flex-grow: 1;
`;

const RightAxisLabel = styled.div`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-weight: ${({ theme }) => theme.fontWeight.SEMI_BOLD};
`;

const LeftAxisLabel = styled(RightAxisLabel)`
  transform-origin: center center;
  transform: rotate(180deg);
`;

const XAxisLabel = styled.div`
  padding-top: 1.5em;
  text-align: center;
  font-weight: ${({ theme }) => theme.fontWeight.SEMI_BOLD};
`;

const leftYAxisTicks = movingAvgRetentionViewbox[3];
const rightYAxisTicks = retentionAggPathViewBox[3];
const xAxisTicks = movingAvgRetentionViewbox[2];
const xAxisOffset = movingAvgRetentionViewbox[0];

const Grid = styled.div`
  --border: 1px solid #353548;
  display: grid;
  grid-template-columns: repeat(${xAxisTicks}, 1fr);
  grid-template-rows: repeat(${leftYAxisTicks}, 1fr);
  border-left: var(--border);
  border-bottom: var(--border);

  > * {
    border-right: var(--border);
    border-top: var(--border);
  }
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  z-index: 2;
`;

const YAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  grid-area: leftAxis;

  &[data-axis='right'] {
    grid-area: rightAxis;
  }
`;

const XAxis = styled.div`
  display: flex;
  justify-content: space-between;
  grid-area: bottomAxis;
`;

const GraphWithAxes = styled.div`
  flex-grow: 1;
  display: grid;
  grid-template-areas:
    'leftAxis graph rightAxis'
    '. bottomAxis .';
  grid-template-rows: 1fr min-content;
  grid-template-columns: min-content 1fr min-content;
`;

const Tick = styled.div`
  position: relative;
  background-color: #353548;
  height: 1px;
  width: 0.5em;
  font-size: var(--font-size--small);
  font-variant-numeric: tabular-nums;

  &[data-align='left'] {
    > * {
      position: absolute;
      top: 0;
      left: 0;
      transform: translate(calc(-100% - 0.2em), -50%);
      text-align: right;
    }
  }

  &[data-align='right'] {
    > * {
      position: absolute;
      top: 0;
      right: 0;
      transform: translate(calc(100% + 0.2em), -50%);
      text-align: left;
    }
  }

  &[data-align='bottom'] {
    height: 0.5em;
    width: 1px;

    > * {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, calc(80%));
      text-align: center;
    }
  }
`;

function Graph() {
  return (
    <GraphOuter>
      <Row>
        <LeftAxisLabel>MOVING_AVG_RETENTION</LeftAxisLabel>
        <GraphWithAxes>
          <YAxis>
            {Array.from({ length: leftYAxisTicks + 1 }, (_, i) => (
              <Tick key={i} data-align="left">
                <div>{leftYAxisTicks - i}</div>
              </Tick>
            ))}
          </YAxis>
          <GraphArea>
            <Grid>
              {Array.from({ length: leftYAxisTicks * xAxisTicks }, (_, i) => (
                <div key={i} />
              ))}
            </Grid>
            <Svg viewBox={movingAvgRetentionViewbox.join(' ')}>
              <path
                d={movingAvgRetentionPath}
                fill="none"
                stroke="#F58518"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2px"
                vectorEffect="non-scaling-stroke"
              />
            </Svg>
            <Svg viewBox={retentionAggPathViewBox.join(' ')}>
              <path
                d={retentionAggPath}
                fill="none"
                stroke="#4C78A8"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2px"
                vectorEffect="non-scaling-stroke"
              />
            </Svg>
          </GraphArea>
          <YAxis data-axis="right">
            {Array.from({ length: rightYAxisTicks + 1 }, (_, i) => (
              <Tick key={i} data-align="right">
                <div>{rightYAxisTicks - i}</div>
              </Tick>
            ))}
          </YAxis>
          <XAxis>
            {Array.from({ length: xAxisTicks + 1 }, (_, i) => (
              <Tick key={i} data-align="bottom">
                <div>{xAxisOffset + i}</div>
              </Tick>
            ))}
          </XAxis>
        </GraphWithAxes>
        <RightAxisLabel>Sum of REVENTION_AGG</RightAxisLabel>
      </Row>
      <XAxisLabel>CHURN_TIME</XAxisLabel>
    </GraphOuter>
  );
}

const CellInner = styled.div`
  padding: 2em 1em 1em 1em;
  font-size: var(--font-size--default);
  line-height: 1;
`;

const CellWithLegend = styled.div`
  display: flex;
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: min(11px, 1.6vw);
`;

const GraphTitle = styled.div`
  margin-bottom: 1.5em;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  padding: 0 2em 0 0;
  font-size: var(--font-size--small);
`;

const LegendItem = styled.div`
  --color: #4c78a8;
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-weight: ${({ theme }) => theme.fontWeight.SEMI_BOLD};

  &::before {
    content: '';
    height: 2px;
    width: 1em;
    border-radius: 999px;
    background-color: var(--color);
  }
`;

export default function GraphCell(
  props: React.ComponentPropsWithoutRef<typeof Cell.Cell>
) {
  return (
    <Cell.Cell {...props}>
      <Cell.RunButton />
      <Cell.Label>All segment retention trend</Cell.Label>
      <CellInner>
        <GraphTitle>All segment retention trend</GraphTitle>
        <CellWithLegend>
          <Graph />
          <Legend>
            <LegendItem style={{ '--color': '#F58518' }}>
              MOVING_AVG_RETENTION
            </LegendItem>
            <LegendItem>Sum of REVENTION_AGG</LegendItem>
          </Legend>
        </CellWithLegend>
      </CellInner>
    </Cell.Cell>
  );
}
